import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { catchError, map, retry, tap } from "rxjs/operators";
import { Observable, throwError } from "rxjs";
import { environment } from "../../../environments/environment";
import { LoggerService } from "./logger.service";
import Swal from "sweetalert2";
import { EventsService } from "./events.service";
import { TenantService } from "./tenant.service";
import { SignInDetails, TokenResponse } from "../models/authentication";
import { TrialUserDetails } from "../models/trial";
import { REGEX_UNDERSCORES } from "../constant/validation";
import { REGEX_FRAGMENT, REGEX_QUERY } from "../constant/urls";
import { NavigationExtras, Router } from "@angular/router";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  private http = inject(HttpClient);
  private eventsService = inject(EventsService);
  private tenantService = inject(TenantService);
  private logger = inject(LoggerService);

  public oafApiUrl: string;

  constructor() {
    this.oafApiUrl = environment.url;
  }

  private uuidv4(): string {
    let d: number = new Date().getTime(); //Timestamp
    let d2: number = (typeof performance !== "undefined" && performance.now && performance.now() * 1000) || 0; //Time in microseconds since page-load or 0 if unsupported
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      let r: number = Math.random() * 16; //random number between 0 and 16
      if (d > 0) {
        //Use timestamp until depleted
        r = (d + r) % 16 | 0;
        d = Math.floor(d / 16);
      } else {
        //Use microseconds since page-load if supported
        r = (d2 + r) % 16 | 0;
        d2 = Math.floor(d2 / 16);
      }
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  public get allowedTenants(): string[] {
    return (localStorage.getItem("allowedTenants") ?? "").split(",").filter(Boolean);
  }

  public set allowedTenants(t: string[]) {
    if ((t?.length ?? 0) === 0) {
      localStorage.removeItem("allowedTenants");
    } else {
      localStorage.setItem("allowedTenants", t.join(","));
    }
  }

  public get tenant(): string {
    return localStorage.getItem("tenant");
  }

  public set tenant(t: string) {
    if (t) {
      localStorage.setItem("tenant", t);
    } else {
      localStorage.removeItem("tenant");
    }
  }

  public getSignInDetails(email: string): Observable<SignInDetails[]> {
    return this.http
      .get<{
        Resources: SignInDetails[];
      }>(`${environment.urlPcsApi}/tenancies-v1/authentication/${email}`)
      .pipe(
        map((response) => response.Resources),
        map((tenants) => tenants.filter((t) => (t?.Tenant || "").toLowerCase() !== "trial")),
        tap((response) => {
          localStorage.setItem("matchingTenants", JSON.stringify(response));
        }),
      );
  }

  public getTrialJWT(
    trialUserDetails: TrialUserDetails,
  ): Observable<{ IdToken: string; AccessToken: string; ExpiresIn?: number }> {
    return this.http
      .get<{
        IdToken: string;
        AccessToken: string;
        ExpiresIn?: number;
      }>(
        `${environment.urlPcsApi}/tenancies-v1/trial/user?${Object.entries(trialUserDetails)
          .filter((e) => !!e[1])
          .map((e) => e.join("="))
          .join("&")}`,
      )
      .pipe(retry(2));
  }

  public onLoginClickCognitoV2SignIn(): void {
    this.eventsService.startLoading("local-login");
    this.getSignInDetails("example@vodafone.com").subscribe((response) => {
      // this.tenant = "vodafone";
      this.logger.log("On Click Sign in with cognito credentials");
      const { ClientId, OAuth2Flow, OAuth2Domain } = response[0];
      const guid: string = this.uuidv4();
      localStorage.setItem("state", guid);
      const urlSigninAAD = `https://${OAuth2Domain}/login?redirect_uri=${encodeURIComponent(
        `${location.protocol}//${location.host}/`,
      )}&response_type=${OAuth2Flow}&client_id=${ClientId}&state=${guid}`;
      window.location.assign(urlSigninAAD);
    });
  }

  public onLoginClickAADGeneric(domain: string, flow: string, idp: string, clientId: string, scope: string): void {
    this.logger.log("On Click Sign in with vodafone credentials");
    const guid: string = this.uuidv4();
    localStorage.setItem("state", guid);
    const urlSigninAAD = `https://${domain}/authorize?identity_provider=${idp}&response_type=${flow}&client_id=${clientId}&redirect_uri=${environment.callback}&scope=${scope}&state=${guid}`;
    window.location.assign(urlSigninAAD);
  }

  public onLoginClickAAD(): void {
    try {
      const { ClientId, Idp, OAuth2Domain, OAuth2Flow, OAuth2Scopes } = JSON.parse(
        localStorage.getItem("matchingTenants") ?? "[]",
      )[0];
      return this.onLoginClickAADGeneric(OAuth2Domain, OAuth2Flow, Idp, ClientId, OAuth2Scopes);
    } catch (e) {
      window.location.assign(`https://${location.host}`);
    }
  }

  public signInAAD(code: string) {
    const redirect = location.hostname === "localhost" ? `http://${location.host}/` : undefined;
    return this.http.post<TokenResponse>(`${this.oafApiUrl}signinaad`, { code, redirect }).pipe(
      catchError((e) => {
        this.logger.error(e);
        return throwError(e);
      }),
    );
  }

  public isLoggedIn(): boolean {
    try {
      const accessTokenString = localStorage.getItem("ACCESS_TOKEN");
      if (accessTokenString) {
        const accessToken = JSON.parse(accessTokenString);
        const IdToken = accessToken.IdToken;
        if (IdToken) {
          const timerString = localStorage.getItem("timer");
          if (timerString) {
            const timer = JSON.parse(timerString);
            if (timer && Date.now() < timer) {
              return true;
            }
          }
        }
      }
    } catch (e) {
      this.logger.error(e);
    }
    return false;
  }

  public saveUrlForLater(): void {
    const value: string = (location?.href ?? "").replace(location?.origin || "", "");

    this.logger.info({
      location,
      origin: location.origin,
      href: location.href,
      value,
    });

    localStorage.setItem("navigateToUrlAfterAuth", value);
  }

  public navigateSignedIn(router: Router): Promise<boolean> {
    let url: string = "";
    const maybeRedirect: string = localStorage.getItem("navigateToUrlAfterAuth");
    if (maybeRedirect) {
      url = maybeRedirect;
      localStorage.removeItem("navigateToUrlAfterAuth");
    } else if (this.tenant === "vodafone") {
      url = "/dashboard#services";
    } else {
      url = "/dashboard#vcloudsmart";
    }

    this.logger.info({
      msg: "Trying to navigate to saved URL",
      url,
      maybeRedirect,
    });

    const fragment: string | undefined = REGEX_FRAGMENT.exec(url)?.[1] ?? "";
    url = url.replace(REGEX_FRAGMENT, "");

    const queryParams: string | undefined = REGEX_QUERY.exec(url)?.[1] ?? "";
    url = url.replace(REGEX_QUERY, "");

    const qps: { [k: string]: string } = Object.fromEntries(
      queryParams
        .split("&")
        .filter(Boolean)
        .map((pair: string) => pair.split("="))
        .filter((pair: string[]) => pair?.length === 2)
        .map((pair: string[]) => pair.map((v) => decodeURIComponent(v))),
    );

    const extras: NavigationExtras = {};

    if (qps) {
      extras.queryParams = qps;
    }

    if (fragment) {
      extras.fragment = fragment;
    }

    this.logger.info({
      msg: "Parsed URL params for navigation",
      url,
      extras,
    });

    return router.navigate([url], extras);
  }

  public getIdToken(): string {
    if (localStorage.getItem("runningCypressTest")) {
      return "asdlkfjaslkdjflaskdjfalsdf";
    }
    try {
      const accessTokenString = localStorage.getItem("ACCESS_TOKEN");
      if (accessTokenString) {
        const accessToken = JSON.parse(accessTokenString);
        const IdToken = accessToken?.IdToken;
        if (IdToken) {
          const timerString = localStorage.getItem("timer");
          if (timerString) {
            const timer = JSON.parse(timerString);
            if (timer && Date.now() < timer) {
              return IdToken;
            }
          }
        }
      }
    } catch (e) {
      this.logger.error(e);
    }
    throw new Error("Session Expired");
  }

  public async autoLogout(): Promise<void> {
    const email = this.email;

    this.logger.log({ email });

    this.eventsService.reset();

    // clear storage after subscribe, this will let the api consume storage data before logout
    this.clearSession();

    this.saveUrlForLater();

    await Swal.fire({
      icon: "warning",
      title: "Session Expired",
      text: `Your session has expired, click on the button below to re-authenticate.`,
      confirmButtonText: "Reauthenticate",
      allowOutsideClick: false,
      confirmButtonColor: this.tenantService.colors.primary,
      footer: "",
    });
    if (environment.local && location.hostname === "localhost") {
      this.onLoginClickCognitoV2SignIn();
    } else {
      this.onLoginClickAAD();
    }
  }

  public get claims(): { [key: string]: any } {
    try {
      const v = localStorage.getItem("ACCESS_TOKEN");
      if (v) {
        const { AccessToken } = JSON.parse(v);
        if (AccessToken) {
          const parts = AccessToken.split(".");
          if (parts.length > 1) {
            const claims = parts[1];
            if (claims) {
              return JSON.parse(atob(claims));
            }
          }
        }
      }
    } catch (e) {}
    return {};
  }

  public get email(): string | undefined {
    try {
      const claims = this.claims;
      const email = claims.username || claims.email || "";
      if (email) {
        return email.replace(REGEX_UNDERSCORES, "");
      }
    } catch (e) {
      this.logger.error("Failed to get email from claims", e);
    }
  }

  public clearSession(): void {
    localStorage.removeItem("ACCESS_TOKEN");
    sessionStorage.removeItem("capturedResponsesVcs");
    sessionStorage.removeItem("capturedResponsesPcsApi");
    localStorage.removeItem("timer");
    sessionStorage.removeItem("expensiveQueries");
    for (const key of Object.keys(sessionStorage).filter((k) => k.startsWith("response-") || k.startsWith("cache-"))) {
      sessionStorage.removeItem(key);
    }
  }

  public isVodacom(): boolean {
    return this.tenant === "vodacom_za";
  }
}
